/* eslint-disable @typescript-eslint/naming-convention */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "@xfrontend/xui-react-plus";
import { LicenseItem } from "#/apis/infra/listLicenses";
import { CustomerItem } from "#/apis/infra/customers";

export type RoleScopeValue = { role: string; scope_type: string; scope_id: string };
type Option = { value: string; label: string };

export const ROLE_NAMES = [
  "SYSTEM_ADMIN", "TERRITORY_ADMIN", "ORG_MANAGER", "ORG_USER", "ORG_AUDITOR",
];

// Each role implies exactly one scope_type, so the UI derives it (no separate scope-type picker).
export function scopeTypeForRole(role: string): string {
  if (role === "SYSTEM_ADMIN") return "SYSTEM";
  if (role === "TERRITORY_ADMIN") return "CUSTOMER";
  return "LICENSE";
}

type RoleScopePickerProps = {
  orgs: LicenseItem[];
  customers: CustomerItem[];
  value: RoleScopeValue;
  onChange: (v: RoleScopeValue) => void;
};

const valueOf = (opts: Option[], v: string): Option | null => opts.find((o) => o.value === v) ?? null;
const picked = (selected: unknown): string => (selected as Option | null)?.value ?? "";

const RoleScopePicker = ({
  orgs, customers, value, onChange,
}: RoleScopePickerProps): JSX.Element => {
  const { t } = useTranslation("management");
  const roleOptions: Option[] = ROLE_NAMES.map((r) => ({ value: r, label: r }));

  // Territory options for a CUSTOMER-scoped role.
  const territoryOptions: Option[] = customers.map((c) => ({ value: c.customer_id, label: c.customer_name }));

  // For a LICENSE-scoped role: cascade Territory -> Organization. The territory list is derived
  // from the org rows (each LicenseItem carries its customer), and the org list is filtered to
  // the chosen territory. The selected territory is derived from the currently-selected org.
  const orgTerritories: Option[] = useMemo(() => {
    const m = new Map<string, string>();
    orgs.forEach((o) => m.set(o.customer_id, o.customer_name));
    return Array.from(m, ([id, label]) => ({ value: id, label }));
  }, [orgs]);

  const currentOrg = orgs.find((o) => o.license_id === value.scope_id);
  const cascadeTerritory = currentOrg?.customer_id ?? orgTerritories[0]?.value ?? "";
  const orgOptions: Option[] = orgs
    .filter((o) => o.customer_id === cascadeTerritory)
    .map((o) => ({ value: o.license_id, label: o.license_name }));

  const handleRole = (role: string): void => {
    const scopeType = scopeTypeForRole(role);
    let scopeId = "SYSTEM";
    if (scopeType === "CUSTOMER") scopeId = customers[0]?.customer_id ?? "";
    if (scopeType === "LICENSE") scopeId = orgs[0]?.license_id ?? "";
    onChange({ role, scope_type: scopeType, scope_id: scopeId });
  };

  // Choosing a territory re-points to the first org under it.
  const handleTerritory = (customerId: string): void => {
    const first = orgs.find((o) => o.customer_id === customerId)?.license_id ?? "";
    onChange({ ...value, scope_id: first });
  };

  return (
    <div>
      <Select
        name="role"
        isSearchable={false}
        options={roleOptions}
        value={valueOf(roleOptions, value.role)}
        onChange={(s: unknown) => handleRole(picked(s))}
      />

      {value.scope_type === "CUSTOMER" && (
        <div style={{ marginTop: 8 }}>
          <Select
            name="territory"
            placeholder={t("scope.territory")}
            options={territoryOptions}
            value={valueOf(territoryOptions, value.scope_id)}
            onChange={(s: unknown) => onChange({ ...value, scope_id: picked(s) })}
          />
        </div>
      )}

      {value.scope_type === "LICENSE" && (
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <div style={{ flex: 1 }}>
            <Select
              name="territory"
              placeholder={t("scope.territory")}
              options={orgTerritories}
              value={valueOf(orgTerritories, cascadeTerritory)}
              onChange={(s: unknown) => handleTerritory(picked(s))}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Select
              name="org"
              placeholder={t("scope.organization")}
              options={orgOptions}
              value={valueOf(orgOptions, value.scope_id)}
              onChange={(s: unknown) => onChange({ ...value, scope_id: picked(s) })}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleScopePicker;
